/**
 * orgScope — the one place the active organization changes, and the one place that knows what
 * "org-scoped client state" means.
 *
 * Every id the UI caches locally — camera, event, agent, rule, enrolled object — is scoped to an
 * organization. Carrying that cache across a switch is not merely stale, it is wrong for the org
 * now on screen: the Objects panel reopens on a known object that does not exist here, the player
 * asks for a camera this org has never heard of, and the AI chat resumes a conversation about
 * someone else's site. So a switch drops all of it.
 *
 * What is NOT dropped is everything that describes this browser rather than this organization:
 * theme, nav-rail width, chat width, per-panel width overrides, and which panels
 * the user likes open. Those are one person's workspace preferences and follow them between orgs.
 *
 * The owners of each cache export their own reset (this module knows no storage keys);
 * `switchActiveOrg` and `reloadIntoOrg` are the funnels that fire them all. Call one of them
 * instead of `authSession.setActiveOrg` — every path that changes the org, including the first
 * pick at login and the known-bad-org recovery in the setup gates, goes through here so no new
 * call site can quietly skip the cleanup.
 */

import { clearStoredCameraNames } from '@/lib/cameraNames';
import { clearStoredPreviews } from '@/lib/cameraPreviews';
import { clearTranscript } from '@/panels/ai_chat/transcript';
import { clearOrgScopedPanelState } from '@/state/ui/shellRecordStorage';

import { authSession } from './authSession';

/**
 * Drop every client-side cache that is keyed by an org entity. Each step is
 * independent and best-effort — one owner throwing (private mode, a corrupt
 * value) must not leave the rest of the state behind.
 */
export function clearOrgScopedState(): void {
    for (const clear of [
        clearOrgScopedPanelState, // panel local props + the remembered camera
        clearTranscript, // AI chat history
        clearStoredPreviews, // camera thumbnail previews
        clearStoredCameraNames, // camera id → last-seen name
    ]) {
        try {
            clear();
        } catch { /* best effort — keep clearing the rest */ }
    }
}

/**
 * Point the app at `orgId` (null = no active org), clearing the outgoing org's
 * state first. A no-op returning false when the org is already active, so the
 * "switch to the org I'm already in" menu click and the login page's
 * remembered-org fast path don't wipe a workspace for nothing.
 *
 * For a path that navigates: org-scoped hooks hold fetched data in memory, so
 * the shell must mount fresh before the new org's data is on screen. A path
 * that reloads calls `reloadIntoOrg`.
 */
export function switchActiveOrg(orgId: string | null): boolean {
    const nextId = orgId || null;
    if (nextId === authSession.getActiveOrg()) {
        return false;
    }
    clearOrgScopedState();
    authSession.setActiveOrg(nextId);
    return true;
}

/**
 * Load the bare shell root in `orgId`, with the no-op of `switchActiveOrg`. The outgoing org stays
 * on screen until the page unloads. The root, not a reload: the current url can name a camera of
 * the outgoing org. The shell rebuilds `panels=` from storage, so the layout survives.
 */
export function reloadIntoOrg(orgId: string | null): void {
    const nextId = orgId || null;
    if (nextId === authSession.getActiveOrg()) {
        return;
    }
    clearOrgScopedState();
    authSession.storeOrgForNextPage(nextId);
    window.location.assign('/');
}
